import React, { useState, useMemo, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import "./WC26TABLE.css";

const CustomDropdown = ({ value, onChange, options, placeholder, searchMode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filtered = options.filter((o) => {
    const s = search.toLowerCase();
    return o.toLowerCase().split(/[\s,\-\/]+/).some((word) => word.startsWith(s));
  });

  const displayValue = value || placeholder;

  return (
    <div className="wc26-custom-dropdown" ref={ref}>
      <div
        className={`wc26-dropdown-trigger ${isOpen ? "wc26-dropdown-open" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={value ? "wc26-dropdown-value" : "wc26-dropdown-placeholder"}>
          {displayValue}
        </span>
        <span className="wc26-dropdown-arrow">▼</span>
      </div>
      {isOpen && (
        <div className="wc26-dropdown-menu">
          <input
            className="wc26-dropdown-search"
            type="text"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          <div className="wc26-dropdown-list">
            <div
              className={`wc26-dropdown-item ${!value ? "wc26-dropdown-item-active" : ""}`}
              onClick={() => { onChange(""); setIsOpen(false); setSearch(""); }}
            >
              {placeholder}
            </div>
            {filtered.map((opt) => (
              <div
                key={opt}
                className={`wc26-dropdown-item ${value === opt ? "wc26-dropdown-item-active" : ""}`}
                onClick={() => { onChange(opt); setIsOpen(false); setSearch(""); }}
              >
                {opt}
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="wc26-dropdown-no-result">No results</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const WC26TABLE = (props) => {
  const [showPopup, setShowPopup] = useState(false);
  const [clicked, setClicked] = useState(false);
  const [dateFilter, setDateFilter] = useState("");
  const [teamFilter, setTeamFilter] = useState("");

  const data = useMemo(() => {
    try {
      return JSON.parse(props?.item?.text);
    } catch {
      return null;
    }
  }, [props?.item?.text]);

  const matches = data?.matchDetails || [];

  const allTeams = useMemo(() => {
    const teams = new Set();
    matches.forEach((m) => {
      if (m?.c1) teams.add(m.c1);
      if (m?.c2) teams.add(m.c2);
    });
    return [...teams].sort();
  }, [matches]);

  const allDates = useMemo(() => {
    return [...new Set(matches.map((m) => m?.date || m?.match_date).filter(Boolean))];
  }, [matches]);

  const filteredMatches = useMemo(() => {
    return matches.filter((m) => {
      const matchDate = !dateFilter || (m?.date || m?.match_date) === dateFilter;
      const matchTeam = !teamFilter || m?.c1 === teamFilter || m?.c2 === teamFilter;
      return matchDate && matchTeam;
    });
  }, [matches, dateFilter, teamFilter]);

  const onClickHandler = () => {
    if (!props.disable) {
      setClicked(true);
      setShowPopup(true);
    }
  };

  const clearFilters = () => {
    setDateFilter("");
    setTeamFilter("");
  };

  if (!data) return null;

  const portalTarget = document.querySelector(".help-chat-box.chatIn-header.help-assistantwrapp.hbc0.maximize");

  return (
    <>
      <button
        className={!props.disable ? "chat-label " : "chat-labelnohover action-tab"}
        style={{
          color: clicked && !showPopup ? "grey" : undefined,
          borderRadius: "44px",
          position: "relative",
          overflow: "visible",
        }}
        onClick={onClickHandler}
      >
        {data?.ctaName || props?.item?.buttonText || "View Football World Cup Schedule"}
      </button>

      {showPopup && portalTarget && ReactDOM.createPortal(
        <div className="wc26-overlay">
          <div className="wc26-popup">
            <div className="wc26-header">
              <h3 className="wc26-title"> {data?.menu || "Football World Cup Schedule"}</h3>
              <button className="wc26-close" onClick={() => { setShowPopup(false); setClicked(false); }}>✕</button>
            </div>
            <div className="wc26-match-count">
            {data?.subMenu || "Below is the match schedule , To add any of the fav channels to view Football World Cup 2026, Please close this window and continue with the journey"}
            </div>

            <div className="wc26-filters">
              <CustomDropdown
                value={teamFilter}
                onChange={(val) => setTeamFilter(val)}
                options={allTeams}
                placeholder="All Teams"
              />

              <CustomDropdown
                value={dateFilter}
                onChange={(val) => setDateFilter(val)}
                options={allDates}
                placeholder="All Dates"
                searchMode="includes"
              />

              {(dateFilter || teamFilter) && (
                <button className="wc26-clear-btn" onClick={clearFilters}>Clear</button>
              )}
            </div>
           
            
            <div className="wc26-table-wrapper">
              {filteredMatches.length > 0 ? (
                filteredMatches.map((match, idx) => (
                  <div className="wc26-match-card" key={idx} style={{ "--i": idx }}>
                    <div className="wc26-match-day">
                      {match?.dayName ? `${match.dayName}, ` : ""}{match?.date || match?.match_date}
                    </div>
                    {match?.c3 ? (
                      <div className="wc26-c3">{match.c3}</div>
                    ) : match?.c1 && match?.c2 ? (
                      <div className="wc26-match-teams">
                        <span className="wc26-team">{match.c1}</span>
                        <span className="wc26-vs">VS</span>
                        <span className="wc26-team">{match.c2}</span>
                      </div>
                    ) : match?.match_details ? (
                      <div className="wc26-match-teams">{match.match_details}</div>
                    ) : null}
                    <div className="wc26-match-info">
                      <span>🕖 {match?.match_time}{match?.venue ? ` , ${match.venue}` : ""}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="wc26-no-results">No matches found</div>
              )}
            </div>
          </div>
        </div>,
        portalTarget
      )}
    </>
  );
};

export default WC26TABLE;
